"use client";

import type { Product } from "../../../_lib/types";

/*
 * 구매 화면이 쓰는 작은 도구 — 번호 다루기 · 적어 둔 값 보관 · 상품 고르기.
 *
 * 🔑 **고른 상품권은 주소(`/?product=…&qty=…`)로 넘깁니다** — 고르기 화면(03)·상품 주소
 *    (`/products/[id]`)·결제 주소(`/checkout`)·로그인 뒤 돌아오기가 전부 같은 길입니다.
 *    세션 저장소와 섞지 않고 하나로 통일했습니다(`docs/base/11` §7-3).
 * 🔑 **적어 둔 번호만 세션 저장소에** 둡니다 — 주소에 싣으면 전화번호가 방문 기록·접속 로그에
 *    남습니다. 탭을 닫으면 사라지고, 주문을 넣으면 지웁니다.
 * ⚠️ 저장소는 막혀 있을 수 있습니다(사생활 보호 창·웹뷰) — 전부 try/catch 이고 실패하면 그냥 없습니다.
 */

const DRAFT_KEY = "tnf.simple.recipient";
const HIDDEN_KEY = "tnf.simple.hiddenRecipients";

export function readDraftPhone(): string {
  try {
    return window.sessionStorage.getItem(DRAFT_KEY) ?? "";
  } catch {
    return "";
  }
}

export function writeDraftPhone(value: string): void {
  try {
    if (value) window.sessionStorage.setItem(DRAFT_KEY, value);
    else window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* 저장소가 막혀 있으면 적어 둔 값만 잃습니다 */
  }
}

/**
 * 최근 보낸 번호 목록에서 «지우기» 한 번호 — 🔑 이 기기에서만 숨깁니다. 주문 기록의 번호는
 * 서버에 그대로 남고(주문의 일부), 목록에서만 빠집니다.
 */
export function readHiddenPhones(): string[] {
  try {
    const raw = JSON.parse(window.localStorage.getItem(HIDDEN_KEY) ?? "[]");
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

export function hidePhone(digits: string): void {
  try {
    const next = [...new Set([...readHiddenPhones(), digits])].slice(-50);
    window.localStorage.setItem(HIDDEN_KEY, JSON.stringify(next));
  } catch {
    /* 위와 같음 */
  }
}

export const phoneDigits = (value: string): string => value.replace(/\D/g, "");

/** 서버 규칙과 같게(`docs/base/11` §7-1 `recipient_phone`) — 숫자 10~11자리, `01` 로 시작. */
export function isMobile(digits: string): boolean {
  return /^01\d{8,9}$/.test(digits);
}

/** 적는 동안 하이픈을 넣어 보여 줍니다 — `01023456789` → `010-2345-6789`. */
export function hyphenate(value: string): string {
  const d = phoneDigits(value).slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

/** 살 수 있는 상품인가 — 재고가 0 인 것(개발 샘플 · 품절)은 진열만 합니다. */
export const purchasable = (p: Product): boolean => p.availableQty === null || p.availableQty > 0;

/** 한 번에 살 수 있는 장수 — 10 장, 재고가 그보다 적으면 재고까지. */
export const qtyLimit = (p: Product | null): number => Math.max(1, Math.min(10, p?.availableQty ?? 10));

/** «2일 전» · «지난달» — 최근 보낸 번호 옆 (시안 04). KST 날짜 기준. */
export function ago(value: string | null | undefined): string {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const day = (d: Date) => Math.floor((d.getTime() + 9 * 3600_000) / 86_400_000);
  const diff = day(new Date()) - day(then);
  if (diff <= 0) return "오늘";
  if (diff === 1) return "어제";
  if (diff < 7) return `${diff}일 전`;
  if (diff < 31) return `${Math.floor(diff / 7)}주 전`;
  if (diff < 62) return "지난달";
  return `${Math.floor(diff / 30)}달 전`;
}

/** `2026-09-20` — 목록의 짧은 날짜 (KST). */
export function shortDate(value: string | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
